import React from "react";
import classNames from "classnames";
import get from "lodash/get";
import TimeAgo from "react-timeago";
import format from "date-fns/format";
import { ResponsiveHeroImage, ReviewRating, LazyLoadImages } from "@quintype/components";
import { CustomLink } from "../custom-link";
import { PropTypes } from "prop-types";
import omit from "@babel/runtime/helpers/objectWithoutProperties";
import { Author } from "./author.js";
import { ImageFallback } from "./image-fallback.js";
import { CollectionName } from "./collection-name.js";
import { truncate } from "../../../utils/truncate-string";
import { altTagOfHeroImages, isExternalStory } from "../../utils";

function formatter(value, unit, suffix, date, defaultFormatter) {
  return format(new Date(date), "DD MMM, YYYY [at] HH:mm A");
}
function getCollectionName(hideSectionName, sectionInlineStyle, [section]) {
  const name = section["display-name"] || section["name"];

  return (
    !hideSectionName && section && <CollectionName section={section} inlineStyle={sectionInlineStyle} name={name} />
  );
}

function getReviewRating(isReviewStory, story) {
  const value = get(story, ["metadata", "review-rating", "value"], "0");

  return isReviewStory && <ReviewRating value={value} />;
}

function getCardDescription(story, config = {}) {
  return (
    config.subheadline && (
      <p className="story-card__description" dangerouslySetInnerHTML={{ __html: story.subheadline }} />
    )
  );
}

export function StoryCard(props) {
  // The `props.story` can be an item from items of a collection or a story itself.
  // assigning it accordinlgy.
  const storyType = get(props, ["story", "type"]);
  const story = storyType === "story" ? get(props, ["story", "story"]) : props.story;
  if (!story) return null;

  const updatedProps = omit(props, ["story"]);

  function getStoryCard() {
    return props.type === "imageBackground" ? (
      <StoryCardBgImage story={story} {...updatedProps} />
    ) : (
      <StoryCardSimple story={story} {...updatedProps} />
    );
  }
  return (
    <div className="story-card-link-wrapper">
      <CustomLink href={`${story.url}`} externalLink={isExternalStory(story)} className="story-card-link" />
      <div className="story-card-content-wrapper">{getStoryCard()}</div>
    </div>
  );
}

StoryCard.propTypes = {
  story: PropTypes.Object,
  config: PropTypes.Object,
  hideSectionName: PropTypes.bool,
  showDate: PropTypes.bool,
  type: PropTypes.string,
  eager: PropTypes.bool
};

function StoryCardBgImage(props) {
  const author = {
    name: get(props.story, ["authors", 0, "name"], props.story["author-name"]),
    id: get(props.story, ["authors", 0, "id"], props.story["author-id"])
  };
  const isReviewStory = props.story["story-template"] === "review";
  const sections = get(props, ["story", "sections"], []);
  const hasImage = get(props, ["config", "image"]);

  return (
    <div className={`story-card ${props.story["story-template"] === "video" ? "story-card__image__video" : ""}`}>
      {hasImage && <ImageRenderComponent {...props} />}
      <div className="story-card__content story-card__content--over-image">
        <div className="story-card__content-head-wrapper">
          {getCollectionName(props.hideSectionName, props.sectionInlineStyle, sections)}
          <h2 className="story-card__heading" data-test-id="story-card-bgimage-headline">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
          </h2>
          {getReviewRating(isReviewStory, props.story)}
        </div>
        {!props.hideAuthor && <Author author={author} />}
      </div>
    </div>
  );
}

StoryCardBgImage.propTypes = {
  story: PropTypes.Object,
  config: PropTypes.Object,
  hideSectionName: PropTypes.bool,
  hideAuthor: PropTypes.bool,
  sectionInlineStyle: PropTypes.Object
};

function StoryCardSimple(props) {
  const author = {
    name: get(props.story, ["authors", 0, "name"], props.story["author-name"]),
    id: get(props.story, ["authors", 0, "id"], props.story["author-id"])
  };
  const isReviewStory = props.story["story-template"] === "review";
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];
  const sections = get(props, ["story", "sections"], []);
  const hasImage = get(props, ["config", "image"]);

  // We can customize this component by passing down a config
  // We can enable Image, Subheadline and Section name if the config values are true.
  // By default we'll hide all.
  return (
    <div className={classNames("story-card", { "story-card--transparent": props.config && props.config.transparent })}>
      {props.pickOfTheDay && (
        <p className="pick-of-the-day" data-test-id="highlighted-title">
          {props.titleofTheHighlightedStory ? truncate(props.titleofTheHighlightedStory, 30, false) : ""}
        </p>
      )}
      {hasImage && <ImageRenderComponent {...props} />}
      <div
        className={classNames("story-card__content", {
          "story-card__content--transparent": props.config && props.config.transparent
        })}
      >
        <div className="story-card__content-head-wrapper">
          {!hasImage && getReviewRating(isReviewStory, props.story)}
          {getCollectionName(props.hideSectionName, props.sectionInlineStyle, sections)}
          <h2 className="story-card__heading" data-test-id="story-card-simple-headline">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
          </h2>
          {hasImage && getReviewRating(isReviewStory, props.story)}
          {getCardDescription(props.story, props.config)}
        </div>
        {!props.hideAuthor && <Author author={author} />}
        {props.showDate && (
          <p className="author__published-date">
            <span className="author__posted-keyword">{!props.showDateOnly && "Posted"}</span>
            <TimeAgo date={publishedTime} formatter={formatter} title={null} />
          </p>
        )}
      </div>
    </div>
  );
}

StoryCardSimple.propTypes = {
  story: PropTypes.Object,
  config: PropTypes.Object,
  pickOfTheDay: PropTypes.bool,
  titleofTheHighlightedStory: PropTypes.string,
  isReviewStory: PropTypes.bool,
  hideSectionName: PropTypes.bool,
  sectionInlineStyle: PropTypes.Object,
  hideAuthor: PropTypes.bool,
  showDate: PropTypes.bool,
  showDateOnly: PropTypes.bool
};

export function ImageRenderComponent(props) {
  const { story, config, eager = false } = props;
  const { imageAspectRatio } = config;
  const aspectRatioClass = imageAspectRatio ? `${imageAspectRatio[0]}x${imageAspectRatio[1]}` : "16x9";
  const imageWidth = get(config, ["imageWidth"], []);
  return story["hero-image-s3-key"] ? (
    <div className={`story-card__image--${aspectRatioClass}`}>
      <figure className={`story-card__image ${story["story-template"] === "video" ? "story-card__image__video" : ""}`}>
        <LazyLoadImages margin={"0px"}>
          <ResponsiveHeroImage
            story={story}
            aspectRatio={imageAspectRatio || [16, 9]}
            alt={altTagOfHeroImages(story)}
            defaultWidth={480}
            widths={imageWidth}
            sizes="(max-width: 500px) 10vw, (max-width: 768px) 48vw, 25vw"
            imgParams={{ auto: ["format", "compress"], fit: "max" }}
            eager={eager}
          />
        </LazyLoadImages>
      </figure>
    </div>
  ) : (
    <div className={`story-card__image--${aspectRatioClass}`}>
      <ImageFallback className="story-card__image" story={story} />
    </div>
  );
}

ImageRenderComponent.propTypes = {
  story: PropTypes.Object,
  config: PropTypes.Object,
  eager: PropTypes.bool
};
